import React, { type FC, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { FormikHelpers, useFormik } from 'formik';
import { useVisitorData } from '@fingerprintjs/fingerprintjs-pro-react';

import type { PassFormValues } from '../../../../types';
import { FeatureFlag } from 'app/api/services/site-configs/types';

import { API, ResponseStatus } from 'app/api';
import { FormControl } from 'app/design-system/components/FormControl';
import PasswordInput from 'app/design-system/components/Input/PasswordInput';
import { StepperContext } from 'app/design-system/components/Stepper/context';
import { useTranslation } from 'app/lang';
import { loginAllTabs } from 'app/lib/auth/authChannel';
import { useCheckFeatureFlagValue, useFeatureFlag } from 'app/lib/feature-flags';
import { ROUTE_PATH } from 'app/lib/routing/constants';
import { CHECK_ICON_TIME_DURATION, PASS_FORM_VALUES } from 'app/modules/SigningNew2/constants';
import { getCookieByKey } from 'app/packages/lib/cookie';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';
import { PROMOCODE_MODAL_PARAM_KEY } from 'app/widgets/PromocodeModal/PromocodeModal';

import { RegContext } from '../../../../context';
import { validatePass } from '../../../../utils/validation/signing-validate';

import dictionary from './ThirdStepForm.dictionary.json';

import styles from './ThirdStepForm.module.scss';

export const ThirdStepForm: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { activeStep } = useContext(StepperContext);
  const { regData, setRegData } = useContext(RegContext);
  const validate = validatePass(t);
  const history = useHistory();
  const isLogged = useSelector(selectIsLoggedIn);

  const { data } = useVisitorData({}, { immediate: true });

  const { sessionToken, phoneToken, completed, disabled, requisite } = regData;

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    setFieldValue,
  } = useFormik({
    initialValues: PASS_FORM_VALUES,
    validate,
    onSubmit: (values: PassFormValues, formikHelpers: FormikHelpers<PassFormValues>) => {
      const { setSubmitting, setFieldError } = formikHelpers;
      const { newPass: password } = values;

      if (sessionToken === null || phoneToken === null) {
        setFieldError('newPass', t(dictionary.senPassError));
        return setSubmitting(false);
      }

      setRegData((prevState) => ({ ...prevState, disabled: true }));
      const subid = getCookieByKey('subid');

      API.registration.sendNewPassword({
        sessionToken,
        phoneToken,
        password,
        requestId: data?.requestId,
        subid,
      })
        .then((response) => {
          if (response.status === ResponseStatus.SUCCESS) {
            setRegData((prevState) => ({ ...prevState, checked: true, newPassword: password }));

            return setTimeout(() => {
              setRegData((prevState) => ({ ...prevState, completed: true, checked: false }));
            }, CHECK_ICON_TIME_DURATION);
          }

          setRegData((prevState) => ({ ...prevState, disabled: false }));
          setSubmitting(false);
          setFieldError('newPass', t(dictionary.senPassError));
        })
        .finally(() => {
          setTimeout(() => {
            dispatch(loginAllTabs({ username: requisite, password }));
          }, CHECK_ICON_TIME_DURATION);
        });
    },
  });

  useEffect(() => {
    setRegData((prevState) => ({ ...prevState, regStep: activeStep }));
    setFieldValue('login', requisite);
  }, []);

  const newPassError = touched.newPass && errors.newPass;

  const isExpRedirectAfterRegistration = useFeatureFlag(
    FeatureFlag.EXP_REDIRECT_AFTER_REGISTRATION,
  );
  const isRedirectToDeposit = useCheckFeatureFlagValue(
    FeatureFlag.EXP_REDIRECT_AFTER_REGISTRATION,
    'deposit',
  );

  useEffect(() => {
    if (completed && isLogged) {
      if (isExpRedirectAfterRegistration && isRedirectToDeposit) {
        history.push(`${ROUTE_PATH.DEPOSIT}?${PROMOCODE_MODAL_PARAM_KEY}`);
      }
      else {
        history.push(`?${PROMOCODE_MODAL_PARAM_KEY}`);
      }
    }
  }, [completed, isLogged]);

  return (
    <>
      <form id={`registrationFormStep_${activeStep}`}
            onSubmit={handleSubmit}
            data-test={`registrationFormStep_${activeStep}`}>
        <div className={styles.newPassInputs}>
          <FormControl label={t(dictionary.newPassLabel)}
                       error={newPassError}
                       className={styles.passwordFormControl}
                       data-test="Modal-registration-newPassLabel">
            <PasswordInput name="newPass"
                           value={values.newPass}
                           error={Boolean(newPassError)}
                           placeholder={t(dictionary.passPlaceholder)}
                           onChange={handleChange}
                           autoFocus
                           disabled={disabled}
                           data-ga-event="signInPassInput"
                           data-test="Modal-registration-newPassInput"
                           className={styles.passwordInput} />
          </FormControl>
        </div>
      </form>
    </>
  );
};
